// NPM
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { omitBy, isNull, isEmpty } from "lodash";
import errorHandler from "Utils/error-handler";
import moment from "moment";

// ANT COMPONENTS
import { Col, Row, Input, Radio } from "antd";
import { AlertBox, Button, SelectInput, TimeRange } from "@life-line-lab/components-react";
import { MoreOutlined } from "@ant-design/icons";

//SCHEMAS
import { ParcelsDropOffCreateSchema } from "./dropoff-create-validation";

// SCSS
import {
  RightCircleOutlined,
  ShoppingCartOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";

interface DropOffModal {
  info?: any;
  onSubmit?: any;
  dropOffLocationList?: any;
  onCancel?: any;
}

const ParcelDropoffModal = ({ info, onSubmit, onCancel, dropOffLocationList }) => {
  const initialInput = {
    name: "",
    dropOffDate: null,
    noOfVehicles: "",
    dropOffTimeFrom: { time: null, timeString: "" },
    dropOffTimeTo: { time: null, timeString: "" },
    dropOffLocation: "",
  };

  const { t } = useTranslation();

  const initialError = {};
  const [input, setInput] = useState(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [parcelCount, setParcelCount] = useState<any>()

  useEffect(() => {
    setParcelCount((info.selectedOrders).length)
    setInput({ ...input, dropOffDate: info.selectedDeliveryDate })
  }, [info])


  const handleSelectChange = (value: string | string[] | {}, name: string) => {
    setError(initialError);
    setInput({ ...input, [name]: value });
  };

  const handleTimeChange = (startTime: any, endTime: any) => {
    if (startTime.time) {
      if ((startTime.time.format('HH:mm')) > moment(input.dropOffTimeTo.time).format("hh:mm a")) {
        setError({ ...error, dropOffTimeTo: 'End time should be later than start time' });
      } else {
        setInput({ ...input, dropOffTimeFrom: startTime });
        setError(initialError);

      }
    } else if (endTime.time) {
      if ((endTime.time.format('HH:mm')) < moment(input.dropOffTimeFrom.time).format("hh:mm a")) {
        setError({ ...error, dropOffTimeTo: 'End time should be later than start time' });
      } else {
        setInput({ ...input, dropOffTimeTo: endTime });
        setError(initialError);
      }
    }
  };

  const handleSubmit = () => {
    const parcelLists = info?.selectedOrders?.map(order => {
      return { id: order._id, trackingNumber: order.trackingNumber, status: 'DROP_OFF_IN_PROGRESS' }
    })

    try {
      const valInput = {
        ...input,
        dropoff: {
          merchantID: info.selectedOrders[0].senderID._id,
          dropOffDate: info.selectedOrders[0].deliveryDate,
          noOfParcels: parcelCount,
          noOfDrivers: input.noOfVehicles,
          parcelList: parcelLists,
          dropOffTimeFrom: input.dropOffTimeFrom ? moment(input.dropOffTimeFrom.time).toISOString() : null,
          dropOffTimeTo: input.dropOffTimeTo ? moment(input.dropOffTimeTo.time).toISOString() : null,
          dropOffLocation: input.dropOffLocation ? input.dropOffLocation : null,
          status: 'OPEN'
        },
        locationID: info.selectedOrders[0].senderLocationID,
        filtersFlag: info.filtersFlag,
      };
      const value = ParcelsDropOffCreateSchema.validateSync(omitBy(valInput, isNull), {
        strict: false,
        abortEarly: true,
        stripUnknown: true,
      });

      onSubmit('dropoff', value);
      onCancel();
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({ ...error, [path]: t(`${value}`, { field: t(`label.${path}`) }) });
    }
  };

  return (
    <>
      <div className="modal-content-title">{t("action.createDropOff").toLocaleUpperCase()}</div>
      <div className="modal-content-text">
        <span className="bold">{t("label.customer").toLocaleUpperCase()}: </span>{info.selectedOrders[0].senderInfo.fullName}
        <br />
        <span className="bold">{t("label.noOfParcel").toLocaleUpperCase()}: </span>
        <span>{parcelCount}</span>
      </div>
      <div className="modal-content-text">
        <SelectInput
          showSearch
          isRequired={true}
          options={[{ label: t("option.all"), value: "" }].concat(
            dropOffLocationList?.map(location => {
              return {
                label: location.locationName,
                value: location._id
              }
            })
          )}
          name="dropOffLocation"
          placeholder={t("placeholder.specialConditions")}
          label={t("label.dropOffLocation")}
          onChange={(value) => handleSelectChange(value, "dropOffLocation")}
        />
        <AlertBox text={error.dropOffLocation} />
        <br />
        <SelectInput
          showSearch
          isRequired={true}
          options={[
            {
              label: "1",
              value: "1",
            },
            {
              label: "2",
              value: "2",
            },
            {
              label: "3",
              value: "3",
            },
          ]}
          name="noOfVehicles"
          placeholder={t("label.noOfVehicles")}
          label={t("label.noOfVehicles")}
          onChange={(value) => handleSelectChange(value, "noOfVehicles")}
        />
        <AlertBox text={error.noOfVehicles} />
        <br />
        {/* <SelectInput
          options={[
            {
              label: "1",
              value: "1",
            },
            {
              label: "2",
              value: "2",
            },
            {
              label: "3",
              value: "3",
            },
          ]}
          name="nameOfDrivers"
          placeholder={t("placeholder.specialConditions")}
          label={t("label.nameOfDrivers")}
          onChange={(value) => handleSelectChange(value, "nameOfDrivers")}
        />
        <AlertBox text={error.nameOfDrivers} /> */}
        <br />
        <Row>
          <Col span={24}>
            <TimeRange
              isRequired={true}
              startLabel={t("label.dropOffBetween")}
              endLabel={t("label.to")}
              value={{
                startTime: input.dropOffTimeFrom,
                endTime: input.dropOffTimeTo,
              }}
              onChange={handleTimeChange}
            />
            <AlertBox text={error.dropOffTimeFrom || error.dropOffTimeTo} />
          </Col>
        </Row>
      </div>

      <div className="mt-30 right modal-end-button">
        <Button
          type={"primary"}
          label={t("action.confirm")}
          // disabled={!isEmpty(error)}
          onClick={() => {
            handleSubmit();
          }}
        />
        <Button
          className="cancel-btn"
          type={"default"}
          label={t("action.cancel")}
          onClick={() => {
            onCancel();
          }}
        />
      </div>
    </>
  );
};

export default ParcelDropoffModal;
